import React, { useEffect, useState } from "react";

export interface ReasonPromptProps {
	/** Shown as the modal title. */
	title: string;
	/** One or two lines explaining what will happen. */
	description?: React.ReactNode;
	/** Label of the textarea (e.g. "Motivo", "¿Qué se entregó?"). */
	label?: string;
	confirmLabel?: string;
	/** Bootstrap button class for the confirm button (btn-danger, btn-success, btn-primary…). */
	confirmClass?: string;
	busy?: boolean;
	error?: string | null;
	/** Max characters of the reason (default 2000); match the DB column of the action. */
	maxLength?: number;
	onConfirm: (reason: string) => void;
	onCancel: () => void;
}

/**
 * Small modal that forces the user to type a reason/note before an action (kits overhaul, Sep 2026):
 * anular, reactivar, registrar entrega, cambiar estado de entrega. Kept dependency-free (plain Bootstrap 5 markup).
 */
const ReasonPrompt: React.FC<ReasonPromptProps> = ({ title, description, label, confirmLabel, confirmClass, busy, error, maxLength, onConfirm, onCancel }) => {
	const [reason, setReason] = useState<string>("");
	const trimmed = reason.trim();
	const valid = trimmed.length >= 3;

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onCancel]);

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
	>
		<div className="card" style={{ width: "100%", maxWidth: "520px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "6px" }}>{title}</h4>
				{description ? <div style={{ color: "var(--wa-apagado)", fontSize: "14px", marginBottom: "12px" }}>{description}</div> : null}
				<div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>{label || "Motivo"} <span style={{ color: "var(--wa-oro)" }}>*</span></div>
				<textarea id="kitReasonPrompt" className="form-control" rows={4} autoFocus disabled={busy} value={reason} maxLength={maxLength || 2000}
					placeholder="Escribe aquí. Quedará en el historial con tu nombre, fecha y hora."
					onChange={(event) => setReason(event.target.value)} style={{ resize: "vertical" }} />
				<div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--wa-apagado)", marginTop: "4px" }}>
					<span>{valid ? "" : "Mínimo 3 caracteres"}</span>
					<span>{trimmed.length}/2000</span>
				</div>
				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className={"btn rounded-pill " + (confirmClass || "btn-primary")} disabled={busy || !valid} onClick={() => onConfirm(trimmed)}>
						{busy ? "Guardando…" : (confirmLabel || "Confirmar")}
					</button>
				</div>
			</div>
		</div>
	</div>;
};

export default ReasonPrompt;
